import React, { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Loading } from "@alm/flow-ds/components";
import { useTranslation } from "react-i18next";
import CustomLink from "components/CustomLink";
import usePagedData, { PageData } from "hooks/usePagedData";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import { ListGroup, ListGroupItem } from "components/ListGroup";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import TasksTableHeadings from "../../TasksTableHeadings";
import TaskCard from "../../TaskCard";
import { useMobileView } from "hooks/useMobileView";
import PaginationBar from "components/PaginationBar";
interface Props {
	search: string;
	requestTasks: (payload?: {
		limit: number;
		offset?: number;
		search?: string;
	}) => Promise<PageData>;
}

const TasksTable = ({ search, requestTasks }: Props) => {
	const { t } = useTranslation();

	const history = useHistory();
	const location = useLocation();

	const { _isMounted } = useCheckMountedStatus();

	const [init, setInit] = useState(false);

	// Current page
	const [currentPage, setCurrentPage] = useState(1);

	// Total nbr of tasks for Pagination comp
	const [total, setTotal] = useState<number | undefined>(0);

	const { paginationLimit } = useMobileView();

	const { state, setData, ready, loading, searching } = usePagedData();

	const { limit } = state;

	const disableOnLoad = state.loading ? "disabled" : "";

	const getMenuSelectors = useCallback(id => {
		return {
			menu: document.querySelector(`#tasksMenu__dropdown__${id}`),
			menuList: document.querySelector(
				`#tasksMenu__dropdown__${id} + div`
			),
		};
	}, []);

	/**
	 * Checks for clicks on the instance item to see if it's not the
	 * instance menu dropdown
	 *
	 * @param e event object
	 * @param id of instance
	 */
	const handleOnClick = useCallback(
		(e, id) => {
			e.preventDefault();
			// Get menu selectors so we know which elements to ignore
			const selectors = getMenuSelectors(id);

			if (
				!selectors.menu?.contains(e.target) &&
				!selectors.menuList?.contains(e.target)
			) {
				addSentryBreadcrumb({
					category: "click",
					message: `Task ${id}`,
				});
				history.push({
					pathname: `/admin/task/${id}`,
					state: { from: location.pathname },
				});
			}
		},
		[getMenuSelectors, history, location.pathname]
	);

	/**
	 * Checks if key is spacebar or enter
	 *
	 * @param e event object
	 * @param id of instance
	 */
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent, id: string) => {
			const target = e.target as HTMLElement;
			// Get menu selectors so we know which elements to ignore
			const selectors = getMenuSelectors(id);

			// If key is spacebar or enter and target isn't instance dropdown then go to instance
			if (
				(e.key === "Enter" || e.keyCode === 32) &&
				!selectors.menu?.contains(target) &&
				!selectors.menuList?.contains(target)
			) {
				addSentryBreadcrumb({
					category: "keydown",
					message: `Task ${id}`,
				});
				e.preventDefault();
				history.push(`/admin/task/${id}`);
			}
		},
		[getMenuSelectors, history]
	);

	/**
	 * Load instances on mount
	 */
	useEffect(() => {
		if (!init) {
			// Load instances from API
			requestTasks({
				limit: 10,
				offset: 0,
				search,
			})
				.then(results => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						setTotal(results.totalCount);
						ready();
						setInit(true);
					}
				})
				.catch(e => console.error(e));
		}
	}, [_isMounted, init, ready, setData, requestTasks, search]);

	useEffect(() => {
		if (init) {
			searching();
			setCurrentPage(1);
			// Load activities from API with search query
			requestTasks({
				limit: 10,
				offset: 0,
				search,
			})
				.then(results => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						setTotal(results.totalCount);
						ready();
					}
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	const pageData = useCallback(
		(page: number) => {
			addSentryBreadcrumb({
				category: "button.click",
				message: "Tasks page",
			});
			loading();
			requestTasks({
				limit: limit,
				offset: (page - 1) * limit,
				search: state.search,
			})
				.then(res => {
					setData(res);
					ready();
				})
				.catch(e => console.error(e));
			setCurrentPage(page);
		},
		[setData, loading, ready, limit, state.search, requestTasks]
	);

	if (!init || state.searching)
		return <Loading className="mt-4 elem__loader--md" />;
	return (
		<>
			{state.results?.length ? (
				<>
					<TasksTableHeadings />
					<ListGroup className="list-group-links">
						{state.results.map((task, index: number) => (
							<ListGroupItem
								className={`task-card task-card--claimed task-card--read ${disableOnLoad}`}
								key={`task__${index}`}>
								<CustomLink
									handleClick={(e: React.MouseEvent) =>
										handleOnClick(e, task.id)
									}
									handleKeyDown={(e: React.KeyboardEvent) =>
										handleKeyDown(e, task.id)
									}
									label={task.id}
									to={`/admin/task/${task.id}`}>
									<TaskCard {...task} />
								</CustomLink>
							</ListGroupItem>
						))}
					</ListGroup>
				</>
			) : (
				!state.loading && (
					<p className="no-tasks">
						{t(
							search.trim()
								? "main.tasks.text.noSearch"
								: "main.tasks.text.noTasks"
						)}
					</p>
				)
			)}
			{total !== undefined && total > limit && !state.searching && (
				<PaginationBar
					currentPage={currentPage}
					limit={limit}
					total={total}
					paginationLimit={paginationLimit}
					onPageChange={page => {
						pageData(page);
					}}
					loading={state.loading}
				/>
			)}
		</>
	);
};

export default TasksTable;
